import Accordion from '../Accordion/Accordion';
import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import BenchmarkDropzone from '../BenchmarkDropzone/BenchmarkDropzone';
import { IBenchmark } from '../../shared/interfaces/IBenchmark';
import { useTranslation } from 'react-i18next';
import BenchmarkCompetitor from '../BenchmarkCompetitor/BenchmarkCompetitor';
import BenchmarkDataCell from '../BenchmarkDataCell/BenchmarkDataCell';
import DeleteIcon from '@mui/icons-material/Delete';
import { constants } from '../../constants/constants';
import Confirm from '../Confirm/Confirm';
import { hasRecommends, hasReplies, IScoreEntry } from '../../shared/interfaces/IScoreEntry';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlatformImage from '../Logo/PlatformImage';
import { InfoOutlined } from '@mui/icons-material';

interface Props extends IBenchmark {
  onDelete: (id: string) => Promise<void>;
  reload: () => Promise<void>;
}

export type TimeFilters = 'day' | 'month' | 'week';

const Benchmark: FC<Props> = ({
  id,
  label,
  platform,
  subject,
  competition,
  onDelete,
  reload,
  createdAt,
  updatedAt,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [isExpanded, setIsExpanded] = useState(false);
  const [deleteIsOpen, setDeleteIsOpen] = useState(false);
  const [subID, setSubID] = useState(subject.id ? subject.id : '');
  const [baseScoreData, setBaseScoreData] = useState<IScoreEntry | null>(null);
  const [isScoreDetailsExpanded, setIsScoreDetailsExpanded] = useState<boolean>(false);
  const [recommendsTimeFilter, setRecommendsTimeFilter] = useState<TimeFilters>('month');
  const [scoreTimeFilter, setScoreTimeFilter] = useState<TimeFilters>('month');
  const [showAverage, setShowAverage] = useState<boolean>(false);

  const borderProps: SxProps<Theme> = {
    borderBottom: `1px solid ${theme.palette.divider}`,
  };

  const subgridProps: SxProps<Theme> = {
    display: 'grid',
    gridTemplateRows: 'subgrid',
    gridRow: '1 / -1',
    px: theme.spacing(2),
  };

  const hasTimeFilterProps: SxProps<Theme> = {
    flexFlow: 'column',
  };

  /* TODO: these two blocks might be able to removed now that we're resolving platform names in <BenchmarkCompetitor> */
  if (platform === 'Ärzte') {
    platform = 'aerzte';
  }
  if (platform === 'Mobile.de') {
    platform = 'mobile';
  }

  useEffect(() => {
    if (!subject.id) return;

    setSubID(subject.id);
  }, [subject.id]);

  let postLabel = null;
  if (subject.label) {
    postLabel = (
      <Tooltip
        title={`${subject.label} (${subject.company})`}
        arrow
      >
        <Typography
          variant="body2"
          sx={{ ml: theme.spacing(2), cursor: 'pointer', fontWeight: 700 }}
        >
          {subject.label.length > 18 ? subject.label.slice(0, 15) + '...' : subject.label}
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Accordion
      expanded={isExpanded}
      onExpandRequest={() => setIsExpanded((prevState) => !prevState)}
      summary={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
          }}
        >
          <PlatformImage
            alt={platform}
            src={constants.platform_logos[platform.toLowerCase()]}
          />
          {postLabel}
          {competition.map((c) => {
            if (!c.label) {
              return null;
            }
            return (
              <Tooltip
                key={c.id}
                title={`${c.label} (${c.company})`}
                arrow
              >
                <Chip
                  label={c.label.length > 18 ? `${c.label.slice(0, 15)}...` : c.label}
                  size="small"
                  sx={{
                    ml: theme.spacing(1),
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
            );
          })}

          {constants.imprecise_platforms.includes(platform.toLowerCase()) && (
            <Box sx={{ display: 'flex', ml: theme.spacing(2), cursor: 'pointer' }}>
              <Tooltip title={t('imprecise_platform_warning')}>
                <InfoOutlined
                  fontSize="small"
                  sx={{ color: theme.palette.primary.main }}
                />
              </Tooltip>
            </Box>
          )}
          <IconButton
            color="error"
            size="small"
            onClick={() => setDeleteIsOpen(true)}
            sx={{ ml: 'auto' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
          <Confirm
            text={t('delete_confirm')}
            open={deleteIsOpen}
            onCancel={() => setDeleteIsOpen(false)}
            onOk={async () => await onDelete(id)}
          />
        </Box>
      }
      details={
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns:
              'minmax(260px, 20%) repeat(4, minmax(16%, 200px)) minmax(0px, 1fr)',
            gridTemplateRows: 'repeat(10, minmax(min-content, auto))',
          }}
        >
          <Box sx={subgridProps}>
            <BenchmarkDataCell
              sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                alignItems: 'center',
                gap: '5px',
              }}
            >
              {competition && competition.length > 0 && (
                <>
                  <Button
                    size="small"
                    variant={!showAverage ? 'contained' : 'outlined'}
                    onClick={() => setShowAverage(false)}
                    sx={{ py: '0.2rem', px: '0.8rem', minWidth: '75px', fontSize: '0.75rem' }}
                  >
                    {t('benchmarking_competitors')}
                  </Button>
                  <Button
                    size="small"
                    variant={showAverage ? 'contained' : 'outlined'}
                    onClick={() => setShowAverage(true)}
                    sx={{ py: '0.2rem', px: '0.8rem', minWidth: '75px', fontSize: '0.75rem' }}
                  >
                    {t('average')}
                  </Button>
                  <Tooltip title={t('benchmarking_tooltip_average_button')}>
                    <InfoOutlined
                      fontSize="small"
                      sx={{ color: theme.palette.primary.main, ml: '0.5rem' }}
                    />
                  </Tooltip>
                </>
              )}
              <Box sx={{ height: 48 }} />
            </BenchmarkDataCell>
            <BenchmarkDataCell sx={borderProps}>
              <Typography variant="body1">{t('score')}</Typography>
            </BenchmarkDataCell>
            <BenchmarkDataCell sx={borderProps}>
              <Tooltip title={t('benchmarking_review_count_tooltip')}>
                <Typography variant="body1">
                  {t('review_count')}
                  <InfoOutlined
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main, ml: '0.5rem' }}
                  />
                </Typography>
              </Tooltip>
              <ExpandMoreIcon
                sx={{
                  marginLeft: 'auto',
                  cursor: 'pointer',
                  translate: 'transform 200ms ease-in-out',
                  transform: isScoreDetailsExpanded ? 'rotate(180deg)' : '',
                }}
                onClick={() => setIsScoreDetailsExpanded(!isScoreDetailsExpanded)}
              />
            </BenchmarkDataCell>
            {baseScoreData && hasReplies(baseScoreData) && (
              <>
                <BenchmarkDataCell sx={borderProps}>
                  <Typography variant="body1">{t('company_response_rate')}</Typography>
                </BenchmarkDataCell>
                <BenchmarkDataCell sx={borderProps}>
                  <Typography variant="body1">{t('company_response_time')}</Typography>
                </BenchmarkDataCell>
              </>
            )}
            <BenchmarkDataCell sx={borderProps}>
              <Typography variant={'body1'}>{t('last_rating')}</Typography>
            </BenchmarkDataCell>
            {baseScoreData && hasRecommends(baseScoreData) && (
              <>
                <BenchmarkDataCell sx={{ ...borderProps, ...hasTimeFilterProps }}>
                  <Typography variant={'body1'}>{t('recommend_rate_last_2_years')}</Typography>
                </BenchmarkDataCell>
                <BenchmarkDataCell sx={{ ...borderProps, ...hasTimeFilterProps }}>
                  <Typography variant={'body1'}>{t('recommend_rate_development')}</Typography>
                  <br />
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button
                      size="small"
                      variant={recommendsTimeFilter === 'day' ? 'contained' : 'outlined'}
                      onClick={() => setRecommendsTimeFilter('day')}
                      sx={{ py: 0, px: 0.5, minWidth: '75px', fontSize: '0.75rem' }}
                    >
                      {t('widget_surveytool_filter_day')}
                    </Button>
                    <Button
                      size="small"
                      variant={recommendsTimeFilter === 'week' ? 'contained' : 'outlined'}
                      onClick={() => setRecommendsTimeFilter('week')}
                      sx={{ py: 0, px: 0.5, minWidth: '75px', fontSize: '0.75rem' }}
                    >
                      {t('widget_surveytool_filter_week')}
                    </Button>
                    <Button
                      size="small"
                      variant={recommendsTimeFilter === 'month' ? 'contained' : 'outlined'}
                      onClick={() => setRecommendsTimeFilter('month')}
                      sx={{ py: 0, px: 0.5, minWidth: '75px', fontSize: '0.75rem' }}
                    >
                      {t('widget_surveytool_filter_month')}
                    </Button>
                  </Box>
                </BenchmarkDataCell>
              </>
            )}
            <BenchmarkDataCell sx={{ ...hasTimeFilterProps }}>
              <Typography variant={'body1'}>{t('score_development')}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button
                  size="small"
                  variant={scoreTimeFilter === 'day' ? 'contained' : 'outlined'}
                  onClick={() => setScoreTimeFilter('day')}
                  sx={{ py: 0, px: 0.5, minWidth: '75px', fontSize: '0.75rem' }}
                >
                  {t('widget_surveytool_filter_day')}
                </Button>
                <Button
                  size="small"
                  variant={scoreTimeFilter === 'week' ? 'contained' : 'outlined'}
                  onClick={() => setScoreTimeFilter('week')}
                  sx={{ py: 0, px: 0.5, minWidth: '75px', fontSize: '0.75rem' }}
                >
                  {t('widget_surveytool_filter_week')}
                </Button>
                <Button
                  size="small"
                  variant={scoreTimeFilter === 'month' ? 'contained' : 'outlined'}
                  onClick={() => setScoreTimeFilter('month')}
                  sx={{ py: 0, px: 0.5, minWidth: '75px', fontSize: '0.75rem' }}
                >
                  {t('widget_surveytool_filter_month')}
                </Button>
              </Box>
            </BenchmarkDataCell>
          </Box>
          <BenchmarkCompetitor
            label={subject.label}
            company={subject.company}
            url={subject.url}
            index={1}
            isSubject={true}
            platform={platform.toLowerCase()}
            sx={{ ...subgridProps, backgroundColor: 'rgba(53,126,139,0.1)' }}
            id={id}
            competitorID={subject.id}
            reload={reload}
            scoreDataChange={(scoreData) => setBaseScoreData(scoreData)}
            isScoreDetailsExpanded={isScoreDetailsExpanded}
            scoreTimeFilter={scoreTimeFilter}
            recommendsTimeFilter={recommendsTimeFilter}
            baseScoreData={baseScoreData}
          />
          {!showAverage ? (
            <>
              {competition.map((c) => {
                return (
                  <BenchmarkCompetitor
                    key={c.id}
                    label={c.label}
                    company={c.company}
                    url={c.url}
                    index={competition.indexOf(c) + 2}
                    isSubject={false}
                    platform={platform.toLowerCase()}
                    benchmarkID={id}
                    competitorID={c.id}
                    reload={reload}
                    isScoreDetailsExpanded={isScoreDetailsExpanded}
                    sx={subgridProps}
                    recommendsTimeFilter={recommendsTimeFilter}
                    scoreTimeFilter={scoreTimeFilter}
                    baseScoreData={baseScoreData}
                  />
                );
              })}
              <Box sx={{ display: 'flex', position: 'relative', gridRow: '1 / -1' }}>
                {competition.length <= 3 && (
                  <BenchmarkDropzone
                    benchmarkID={subID}
                    subject={subject}
                    companyUrl={subject.url}
                    competitors={competition}
                    label={label}
                    id={id}
                    platform={platform}
                    reload={reload}
                  />
                )}
              </Box>
            </>
          ) : (
            <BenchmarkCompetitor
              key={'__avg__'}
              label={t('average_values')}
              company={t('average_values')}
              url={''}
              index={-1}
              isSubject={false}
              platform={platform.toLowerCase()}
              competition={competition.map((comp) => comp.company)}
              benchmarkID={id}
              competitorID={'__avg__'}
              reload={reload}
              isScoreDetailsExpanded={isScoreDetailsExpanded}
              sx={subgridProps}
              recommendsTimeFilter={recommendsTimeFilter}
              scoreTimeFilter={scoreTimeFilter}
              baseScoreData={baseScoreData}
            />
          )}
        </Box>
      }
    />
  );
};

export default Benchmark;
